<template>
      <div class="item flex flex-col" :class="[placementGenerator, card.remove ? 'deleted' : null]">
            <div v-if="editModeStore.isEdit" class="placement px-4 py-2 bg-gray-100 flex items-start">
                  <div class="size flex items-start pr-4 border-r">
                        <div class="-rotate-90 label p-0 self-center text-gray-400 text-sm transform">
                              Size
                        </div>
                        <div class="flex flex-col space-y-2">
                              <div class="width flex items-center">
                                    <div class="label flex">
                                          <vue-feather size="14px" type="maximize-2" :stroke="theme.colors.steelBlue" class="transform rotate-45" />
                                    </div>
                                    <div class="buttons flex ">
                                          <div @click="sizeChange('width','minus')">
                                                <vue-feather size="18px" stroke-width="1" type="minus" :class="{'disabled': card.placement[0] < 2}" />
                                          </div>
                                          <div @click="sizeChange('width', 'plus')">
                                                <vue-feather size="18px" stroke-width="1" type="plus" :class="{'disabled': card.placement[0] > 3}" />
                                          </div>
                                    </div>
                              </div>
                              <div class="height flex items-center">
                                    <div class="label flex">
                                          <vue-feather size="14px" type="maximize-2" :stroke="theme.colors.steelBlue" class="transform -rotate-45" />
                                    </div>
                                    <div class="buttons flex ">
                                          <span @click="sizeChange('height', 'minus')">
                                                <vue-feather size="18px" stroke-width="1" type="minus" :class="{'disabled': card.placement[1] < 2}" />
                                          </span>
                                          <span @click="sizeChange('height', 'plus')">
                                                <vue-feather size="18px" stroke-width="1" type="plus" />
                                          </span>
                                    </div>
                              </div>
                        </div>
                  </div>
                  <div class="order flex items-start">
                        <div class="-rotate-90 label p-0 self-center text-gray-400 text-sm transform">
                              Order
                        </div>
                        <div class="buttons flex flex-col ">
                          <span @click="order('minus')">
                                    <vue-feather size="18px" stroke-width="1" type="chevron-up" :class="{ 'disabled' : isFirstLast === 'first' }" />
                          </span>
                          <span @click="order('plus')">
                                    <vue-feather size="18px" stroke-width="1" type="chevron-down" :class="{'disabled': isFirstLast === 'last'}" />
                          </span>
                        </div>
                  </div>
                  <div class="ml-auto tools flex items-center self-center rounded bg-gray-200">
                        <div v-tippy="{ content: 'Edit card', appendTo: 'parent', theme: 'trg' }" class="buttons edit " @click="editCard(card)">
                              <vue-feather size="18px" stroke-width="1" type="edit-2" class="flex" />
                        </div>
                    <VDropdown v-tippy="{ content: 'Delete card', appendTo: 'parent', theme: 'trg' }" :distance="6">
                      <div class=" flex items-center buttons">
                        <vue-feather size="18px" stroke-width="1" type="trash" />
                      </div>
                      <template #popper>
                        <p slot="header" class="border-b border-gray-100 px-3 py-2">
                          Card will be deleted! Are you sure?
                        </p>
                        <ul v-close-popper class="flex">
                          <li class="flex-1 justify-center p-3 flex items-center space-x-3 cursor-pointer hover:bg-gray-100" @click="removeCard()"><vue-feather type="check" />
                            Yes
                          </li>
                          <li class="flex-1 justify-center p-3 flex items-center space-x-3 cursor-pointer hover:bg-gray-100"><vue-feather type="x" />
                            No
                          </li>
                        </ul>
                      </template>
                    </VDropdown>
                  </div>
            </div>
            <div class="header px-4 py-2 flex justify-between">
                  <div class="name w-full">
                          <template v-if="!loadingStore.isLoading">
                                {{ card.name }} <a v-tippy="{ content: 'View report', appendTo: 'parent', theme: 'trg' }" class="btn-view-report bg-gray-100 font-light has-tooltip hover:bg-navy hover:text-white m-2 px-2 text-gray-500 text-sm text-uppercase rounded-full transition" :href="'/reports?id=' + reportId">View</a>
                          </template>
                          <skeleton-block v-else-if="!!loadingStore.isLoading" height="20px" :effect="effect" />
                  </div>
            </div>
            <div class="report px-4 py-2 flex-1">
                  <skeleton-block v-if="!!loadingStore.isLoading" tag="div" width="100%" height="225px" :effect="effect" />
                  <chart-container
                      v-if="!!reportId"
                      :key="'card-'+ card.id"
                      :data="dataForChart()"
                      :responsive="true"
                      :show-legend="showLegend"
                      :legend-position="legendPosition"
                      :index-axis="indexAxis"
                      :chart-type="chartType"
                      :guid="'card-'+ card.id"
                  />
            </div>
  </div>
</template>

<script>
import ChartContainer from './ChartContainer'
import ModalNewCard from '../components/ModalNewCard'
import { SkeletonBlock } from 'skeleton-elements/vue'
import resolveConfig from 'tailwindcss/resolveConfig'
import tailwindConfig from '../../tailwind.config.js'
import {useDashboardStore} from "@/store/dashboard";
import {useLoadingStore} from "@/store/loading";
import {useEditModeStore} from "@/store/edit";
import {useReportStore} from "@/store/report";
import {useModalStore} from "@/store/modal";


export default {
      setup() {
            const dashboardStore = useDashboardStore()
            const loadingStore = useLoadingStore()
            const editModeStore = useEditModeStore()
            const reportStore = useReportStore()
            const modalStore = useModalStore()

            return { dashboardStore, loadingStore, editModeStore, reportStore, modalStore }
      },
      name: 'Card',
      components: {
            SkeletonBlock,
            ChartContainer,
      },
      props: {
            index: {
                  type: Number,
                  required: true
            },
            card: {
                  type: Object,
                  required: true,
            }
      },
      data(){
            const {theme} = resolveConfig(tailwindConfig)
            return {
              theme: theme,
              effect: 'wave'
            }
      },
      computed: {
            cardWidth(){
                  return this.dashboardStore.findItemByItemId(this.card.id).placement[0]
            },
            cardHeight(){
                  return this.dashboardStore.findItemByItemId(this.card.id).placement[1]
            },
            placementGenerator() {
                  const width = this.cardWidth,
                        height = this.cardHeight,
                        css_width = 'column-s' + width + ' ',
                        css_height = 'row-s' + height + ' ';
                  return css_width + css_height
            },
            isFirstLast(){
                  if(this.index == 0) return 'first'
                  else if(this.index == this.dashboardStore.allItems.length - 1) return 'last'
                  else return 'middle'
            },
            reportId (){
                  return this.card.report_id
            },
            indexAxis(){
                  if (this.dashboardStore.findItemByItemId(this.card.id).chart_options.type === 4) {
                        return 'y'
                  } else {
                        return 'x'
                  }
            },
            showLegend(){
                  return this.card.chart_options.show_legend != undefined ? this.card.chart_options.show_legend : true
            },
            legendPosition(){
                  return this.card.chart_options.legend_position != undefined ? this.card.chart_options.legend_position : 'top'
            },
            chartType(){
                  return this.dashboardStore.findItemByItemId(this.card.id).chart_options.type != undefined ? this.dashboardStore.findItemByItemId(this.card.id).chart_options.type : this.card.chart_id
            },
      },
      methods: {
            dataForChart(){
                  let output = this.reportStore.chart(this.reportId)
                  if(output != undefined) {
                        if(this.chartType != undefined){
                              output.chart_type_id = this.chartType
                        }
                  }
                  return output
            },
            order(direction){
                  let data;
                  if(direction === 'minus') {
                        if(this.isFirstLast !== 'first'){
                              data = {oldIndex: this.index, newIndex: this.index - 1}
                              this.dashboardStore.changeOrder(data)
                        }

                  } else if(direction === 'plus') {
                        if(this.isFirstLast !== 'last'){
                              data = {oldIndex: this.index, newIndex: this.index + 1}
                              this.dashboardStore.changeOrder(data)
                        }
                  }
            },
            sizeChange(dimension, val){
                  let size = this.card.placement
                  let changed = false
                  const valWidth = size[0],
                        valHeight = size[1],
                        maxWidth = 4
                  if(dimension == 'width'){
                        if(val == 'minus') {
                              if(valWidth > 1) {
                                    size[0] -= 1
                                    changed = true
                              }
                        } else if(val == 'plus') {
                              if(valWidth < maxWidth) {
                                    size[0] += 1
                                    changed = true
                              }
                        }
                  }

                  if(dimension == 'height'){
                        if(val == 'minus') {
                              if(valHeight > 1) {
                                    size[1] -= 1
                                    changed = true
                              }
                        } else if(val == 'plus') {
                              size[1] += 1
                              changed = true
                        }
                  }

                  if (changed) {
                        this.dashboardStore.changeSize({id: this.card.id, size})
                  }
            },
            editCard(){
                  this.modalStore.open(ModalNewCard, {id: this.card.id, item: this.card});
            },
            removeCard(){
                  let deletedCard = this.card;
                  deletedCard.remove = true
                  this.dashboardStore.setCard({output:deletedCard, edit: true})
            }
      },
}
</script>

<style lang="sass">
.row-s1
      grid-row: span 1
      .report
            max-height: 250px
.row-s2
      grid-row: span 2
      .report
            max-height: 500px
.row-s3
      grid-row: span 3
      .report
            max-height: 750px
.row-s4
      grid-row: span 4
      .report
            max-height: 900px
.column-s1
      grid-column: span 1
.column-s2
      grid-column: span 2
.column-s3
      grid-column: span 3
.column-s4
      grid-column: span 4
@media screen and (max-width: 1024px)
      .column-s1
            grid-column: span 2
.item
      @apply bg-white
      @apply rounded
      @apply shadow-md
      overflow: hidden
      min-width: 0
      transition: all .4s
      .placement
            .tools
                  .edit
                        i
                              display: flex
      &.deleted
            display: none
      .name
            @apply font-bold
            .btn-view-report:hover
                  color: white !important
      .buttons
            @apply bg-gray-200
            @apply rounded-sm 
            // @apply hover:bg-navy
            // @apply hover:text-white
            @apply cursor-pointer
            i
                  display: block
                  @apply transition
                  @apply p-1
                  // @apply border
                  // @apply border-navy
                  @apply rounded-sm
                  // @apply text-navy
                  &:hover
                        @apply bg-pureNavy
                        color: white
                  &.disabled
                        @apply cursor-not-allowed
                        @apply text-gray-300
                        @apply border-gray-300
                        &:hover
                              @apply bg-gray-200
</style>